import React, { useEffect } from 'react';
import '../styles/PrivacyPolicy.css'; // Import the CSS file for styling

const PrivacyPolicy = () => {
  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const lastUpdated = 'March 15, 2026';

  return (
    <div className="privacy-page">
      <div className="privacy-container">
        {/* Page Header */}
        <div className="privacy-header">
          <span className="privacy-tag">Privacy Policy</span>
          <h1 className="privacy-title">Your Privacy Matters to Us</h1>
          <p className="privacy-subtitle">
            We are committed to protecting your personal information and being transparent about how we use it.
          </p>
          <div className="privacy-meta">
            <span className="privacy-meta-item">📅 Last Updated: {lastUpdated}</span>
            <span className="privacy-meta-divider">|</span>
            <span className="privacy-meta-item">📋 Effective: Immediately</span>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="privacy-toc">
          <h3>Table of Contents</h3>
          <ul className="privacy-toc-list">
            <li><a href="#section-1">1. Information We Collect</a></li>
            <li><a href="#section-2">2. How We Use Your Information</a></li>
            <li><a href="#section-3">3. Information Sharing</a></li>
            <li><a href="#section-4">4. Data Security</a></li>
            <li><a href="#section-5">5. Your Rights</a></li>
            <li><a href="#section-6">6. Cookies & Tracking</a></li>
            <li><a href="#section-7">7. Third-Party Services</a></li>
            <li><a href="#section-8">8. Children's Privacy</a></li>
            <li><a href="#section-9">9. Changes to This Policy</a></li>
            <li><a href="#section-10">10. Contact Us</a></li>
          </ul>
        </div>

        {/* Privacy Content */}
        <div className="privacy-content">
          {/* Section 1 */}
          <section id="section-1" className="privacy-section">
            <h2 className="privacy-section-title">1. Information We Collect</h2>
            <p className="privacy-section-text">
              We collect information that you provide directly to us when you:
            </p>
            <ul className="privacy-section-list">
              <li>Request a quote or book our services</li>
              <li>Contact us via phone, email, or our contact form</li>
              <li>Subscribe to our newsletter or promotional communications</li>
              <li>Fill out surveys or provide feedback</li>
              <li>Interact with us on social media</li>
            </ul>
            <p className="privacy-section-text">
              The types of information we may collect include:
            </p>
            <ul className="privacy-section-list">
              <li><strong>Personal Information:</strong> Name, email address, phone number, physical address</li>
              <li><strong>Service Information:</strong> Details about your pest control needs and property</li>
              <li><strong>Payment Information:</strong> Billing details (processed securely through payment partners)</li>
              <li><strong>Communication Records:</strong> Call recordings, emails, and chat transcripts</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section id="section-2" className="privacy-section">
            <h2 className="privacy-section-title">2. How We Use Your Information</h2>
            <p className="privacy-section-text">
              We use the information we collect to:
            </p>
            <ul className="privacy-section-list">
              <li>Provide, maintain, and improve our pest control services</li>
              <li>Process your bookings, payments, and service requests</li>
              <li>Send you service confirmations, updates, and appointment reminders</li>
              <li>Respond to your comments, questions, and requests</li>
              <li>Send you promotional materials and special offers (with your consent)</li>
              <li>Monitor and analyze trends, usage, and activities in connection with our services</li>
              <li>Detect, investigate, and prevent fraudulent transactions and other illegal activities</li>
              <li>Comply with legal obligations and enforce our terms of service</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section id="section-3" className="privacy-section">
            <h2 className="privacy-section-title">3. Information Sharing</h2>
            <p className="privacy-section-text">
              We do not sell, trade, or rent your personal information to third parties. However, we may share your information in the following circumstances:
            </p>
            <ul className="privacy-section-list">
              <li><strong>Service Providers:</strong> With trusted third-party vendors who assist us in operating our business (e.g., payment processors, scheduling platforms)</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulation</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>With Your Consent:</strong> When you have given us explicit permission to share your information</li>
            </ul>
            <p className="privacy-section-text">
              We require all third-party service providers to maintain the confidentiality of your information and use it only for the purposes we specify.
            </p>
          </section>

          {/* Section 4 */}
          <section id="section-4" className="privacy-section">
            <h2 className="privacy-section-title">4. Data Security</h2>
            <p className="privacy-section-text">
              We implement a variety of security measures to maintain the safety of your personal information:
            </p>
            <ul className="privacy-section-list">
              <li><strong>Encryption:</strong> All sensitive information is transmitted via Secure Socket Layer (SSL) technology</li>
              <li><strong>Access Control:</strong> Limited access to personal information on a need-to-know basis</li>
              <li><strong>Regular Audits:</strong> Periodic security assessments and vulnerability testing</li>
              <li><strong>Data Minimization:</strong> We only collect and retain information necessary for business purposes</li>
              <li><strong>Secure Storage:</strong> Your information is stored on secure servers with firewalls and monitoring</li>
            </ul>
            <p className="privacy-section-text">
              While we strive to protect your personal information, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security but continuously work to protect your data.
            </p>
          </section>

          {/* Section 5 */}
          <section id="section-5" className="privacy-section">
            <h2 className="privacy-section-title">5. Your Rights</h2>
            <p className="privacy-section-text">
              Depending on your location, you may have the following rights regarding your personal information:
            </p>
            <ul className="privacy-section-list">
              <li><strong>Right to Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your personal information (subject to legal obligations)</li>
              <li><strong>Right to Restrict Processing:</strong> Request that we limit how we use your information</li>
              <li><strong>Right to Data Portability:</strong> Request a copy of your data in a machine-readable format</li>
              <li><strong>Right to Object:</strong> Object to the processing of your information for marketing purposes</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw your consent at any time (where we rely on consent)</li>
            </ul>
            <p className="privacy-section-text">
              To exercise any of these rights, please contact us using the information in Section 10.
            </p>
          </section>

          {/* Section 6 */}
          <section id="section-6" className="privacy-section">
            <h2 className="privacy-section-title">6. Cookies & Tracking</h2>
            <p className="privacy-section-text">
              We use cookies and similar tracking technologies to enhance your experience on our website. We use:
            </p>
            <ul className="privacy-section-list">
              <li><strong>Essential Cookies:</strong> Necessary for the website to function properly</li>
              <li><strong>Performance Cookies:</strong> Help us understand how visitors interact with our website</li>
              <li><strong>Functionality Cookies:</strong> Remember your preferences and settings</li>
              <li><strong>Targeting Cookies:</strong> Used to deliver relevant advertisements (with your consent)</li>
            </ul>
            <p className="privacy-section-text">
              You can manage your cookie preferences through your browser settings. Most browsers allow you to refuse cookies or alert you when cookies are being sent.
            </p>
          </section>

          {/* Section 7 */}
          <section id="section-7" className="privacy-section">
            <h2 className="privacy-section-title">7. Third-Party Services</h2>
            <p className="privacy-section-text">
              Our website may contain links to third-party websites and services. We are not responsible for the privacy practices of these third parties. We encourage you to review the privacy policies of any third-party services you use.
            </p>
            <p className="privacy-section-text">
              We use the following third-party services:
            </p>
            <ul className="privacy-section-list">
              <li><strong>Payment Processing:</strong> Stripe, PayPal (for secure payment handling)</li>
              <li><strong>Analytics:</strong> Google Analytics (to understand website usage)</li>
              <li><strong>Scheduling:</strong> Calendly (for appointment booking)</li>
              <li><strong>Email Marketing:</strong> Mailchimp (for newsletters and communications)</li>
            </ul>
          </section>

          {/* Section 8 */}
          <section id="section-8" className="privacy-section">
            <h2 className="privacy-section-title">8. Children's Privacy</h2>
            <p className="privacy-section-text">
              Our services are not directed to children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately, and we will take steps to remove that information.
            </p>
          </section>

          {/* Section 9 */}
          <section id="section-9" className="privacy-section">
            <h2 className="privacy-section-title">9. Changes to This Policy</h2>
            <p className="privacy-section-text">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. We encourage you to review this Privacy Policy periodically.
            </p>
            <p className="privacy-section-text">
              If we make material changes to this policy, we will provide additional notice through our website or via email.
            </p>
          </section>

          {/* Section 10 */}
          <section id="section-10" className="privacy-section">
            <h2 className="privacy-section-title">10. Contact Us</h2>
            <p className="privacy-section-text">
              If you have any questions, concerns, or requests regarding this Privacy Policy, please contact us:
            </p>
            <div className="privacy-contact-info">
              <div className="privacy-contact-item">
                <span className="privacy-contact-icon">📞</span>
                <div>
                  <strong>Phone:</strong>
                  <a href="tel:+15551234567" className="privacy-contact-link">(555) 123-4567</a>
                </div>
              </div>
              <div className="privacy-contact-item">
                <span className="privacy-contact-icon">✉️</span>
                <div>
                  <strong>Email:</strong>
                  <a href="mailto:privacy@pestcontroltoday.com" className="privacy-contact-link">privacy@pestcontroltoday.com</a>
                </div>
              </div>
              <div className="privacy-contact-item">
                <span className="privacy-contact-icon">📍</span>
                <div>
                  <strong>Address:</strong>
                  <span className="privacy-contact-text">123 Pest Free Lane, Suite 200, New York, NY 10001</span>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Back to Top */}
        <div className="privacy-back-top">
          <button 
            className="privacy-back-top-btn"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            ↑ Back to Top
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;